// src/pages/Swap/Swap.js 
/* global BigInt */

import React, { useContext, useState, useEffect, useRef, useCallback, useMemo  } from 'react';
import { useLocation } from 'react-router-dom';
import { formatUnits, ethers } from 'ethers';
import { SettingsContext } from '../../contexts/SettingsContext';
import { WalletContext } from '../../contexts/WalletContext';
import { NetworkContext } from '../../contexts/NetworkContext';
import TokenListModal from '../../components/TokenListModal';
import { createNativeTokenFromChain, selectPlaceholder, ETH_ADDRESS } from './utils/SwapConstants';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import TransactionNotificationWrapper from '../../components/TransactionNotification/TransactionNotificationWrapper';
import { isUsingSushiSwap } from '../../services/sushiswapApi';
import { useTranslation } from 'react-i18next';
import { getRouterAddress, getChainApiType, getRouterName } from '../../utils/chainUtils';
import './Swap.css';

// Import modular components
import SwapHeader from './components/SwapHeader';
import SwapForm from './components/SwapForm';
import SwapActions from './components/SwapActions';
import SwapSettings from './components/SwapSettings';

// Import constants and utils
import { isNativeToken, getBitDogSwapContract, checkAllowance, formatAmountForDisplay } from './utils/SwapUtils';

// Import custom hooks
import useTokenBalances from './hooks/useTokenBalances';
import useTokenApproval from './hooks/useTokenApproval';
import useExchangeRate from './hooks/useExchangeRate';
import useSwapExecution from './hooks/useSwapExecution';
import useWrapUnwrap from './hooks/useWrapUnwrap';

/**
 * Main Swap component that integrates all swap functionality
 */
const Swap = ({ deepLinkTokenData }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentWallet, provider, address } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  const { slippage, gasOption, customGasPrice, autoConfirm, deadline } = useContext(SettingsContext);

  // State for BitDogSwap fee information
  const [aggregatorFeePercentage, setAggregatorFeePercentage] = useState(0.5); // Default 0.5%
  const [collectFeesInWeth, setCollectFeesInWeth] = useState(false);

  // Initialize tokens based on current chain
  const [inputToken, setInputToken] = useState(() => {
    // This function runs once during initialization
    return currentChain ? createNativeTokenFromChain(currentChain) : selectPlaceholder;
  });
  const [outputToken, setOutputToken] = useState(selectPlaceholder);

  // State to track if we're in the middle of a chain change
  const [isChangingChain, setIsChangingChain] = useState(false);
  
  // Reference to track the last chain we were connected to
  const lastChainRef = useRef(currentChain?.key || null);

  // Two-way amounts for the swap
  const [amountIn, setAmountIn] = useState('');
  const [amountOut, setAmountOut] = useState('');
  const [minimumAmountOut, setMinimumAmountOut] = useState(''); // Add minimum amount received after slippage
  const [lastEdited, setLastEdited] = useState('input');

  // Gas price state
  const [dynamicGasPrice, setDynamicGasPrice] = useState(null);
  
  // Auto-refresh timer state
  const refreshTimerRef = useRef(null);
  const notificationRef = useRef(null);

  // Token list modal state
  const [isTokenModalOpen, setIsTokenModalOpen] = useState(false);
  const [selectedSide, setSelectedSide] = useState(null);

  // Wrap/Unwrap state
  const [wrapUnwrapOperation, setWrapUnwrapOperation] = useState(null);

  // Use custom hooks for state management
  const { 
    inputTokenBalance, 
    outputTokenBalance,
    refreshInputBalance,
    refreshOutputBalance,
    refreshAllBalances
  } = useTokenBalances(
    provider, 
    address, 
    inputToken, 
    outputToken
  );

// Add useMemo to extract and verify wallet address
const userWalletAddress = useMemo(() => {
  if (!currentWallet?.privateKey) {
    return null;
  }
  
  try {
    // Create wallet instance to get an accurate address
    const wallet = new ethers.Wallet(currentWallet.privateKey);
    return wallet.address;
  } catch (error) {
    console.error("Error getting wallet address:", error);
    return null;
  }
}, [currentWallet]);

// Pass userWalletAddress to useExchangeRate
const { 
  exchangeRate, 
  priceImpact, 
  isLoading, 
  swapError, 
  bestRouter, 
  bestDestRouter,
  routeSummary, 
  isRefreshing,
  fetchExchangeRate, 
  handleManualRefresh,
  setSwapError: setExchangeRateError,
  setExchangeRate, // Import the setter
  setPriceImpact // Import the setter
} = useExchangeRate(
  provider, 
  currentChain, 
  inputToken,
  outputToken,
  amountIn,
  userWalletAddress, // Use the stable wallet address
  isChangingChain // Pass the isChangingChain flag to prevent premature fetches
);

  // Get the approval hook with updated handling for confirmation modal
  const {
    needsApproval,
    isCheckingApproval,
    isApproving,
    approvalError,
    approvalConfirmationModalOpen,
    approvalConfirmationDetails,
    approvalTxnHash,
    approveToken,
    executeApproval,
    setApprovalConfirmationModalOpen,
    refreshApprovalStatus
  } = useTokenApproval(
    provider,
    currentWallet,
    address,
    inputToken,
    currentChain,
    amountIn,
    currentChain?.explorerUrl,
    notificationRef,
    autoConfirm
  );

  const {
    isSwapping,
    txnHash,
    swapError: executionError,
    tempGasOption,
    tempGasPrice,
    confirmationDetails,
    isConfirmationModalOpen,
    setSwapError: setExecutionError,
    setIsConfirmationModalOpen,
    setTxnHash,
    executeSwap,
    prepareSwapConfirmation,
    handleGasSettingChange
  } = useSwapExecution(
    provider,
    currentWallet,
    currentChain,
    notificationRef,
    autoConfirm
  );

  // Use wrap/unwrap hook - UPDATED to pass inputToken and outputToken
  const {
    isWrapUnwrapOperation,
    getCurrentOperation, // Import the new function
    prepareConfirmation: prepareWrapUnwrapConfirmation,
    handleConfirmation: handleWrapUnwrapConfirmation,
    isProcessing: isWrapUnwrapping,
    txnHash: wrapUnwrapTxnHash,
    error: wrapUnwrapError,
    setError: setWrapUnwrapError,
    confirmationDetails: wrapUnwrapConfirmationDetails,
    isConfirmationModalOpen: isWrapUnwrapModalOpen,
    setIsConfirmationModalOpen: setIsWrapUnwrapModalOpen
  } = useWrapUnwrap(
    provider,
    currentWallet,
    currentChain,
    notificationRef,
    autoConfirm,
    inputToken, // Pass current inputToken
    outputToken  // Pass current outputToken
  );

  // Merge error states
  const combinedSwapError = approvalError || executionError || swapError || wrapUnwrapError;

  // Function to reset the swap state completely
  const resetSwapState = () => {
    // Reset token selections - keeping only native token on input side
    setInputToken(currentChain ? createNativeTokenFromChain(currentChain) : selectPlaceholder);
    setOutputToken(selectPlaceholder);
    
    // Reset amounts
    setAmountIn('');
    setAmountOut('');
    setMinimumAmountOut('');
    
    // Reset errors
    if (setExchangeRateError) setExchangeRateError(null);
    if (setExecutionError) setExecutionError(null);
    if (setWrapUnwrapError) setWrapUnwrapError(null);
    
    // Reset transaction state
    setTxnHash(null);
    
    // Reset other flags
    setLastEdited('input');
    setWrapUnwrapOperation(null);
  };

  // Handle network changes - completely reset UI state when chain changes with proper synchronization
  useEffect(() => {
    // Detect chain change
    if (currentChain?.key !== lastChainRef.current) {
      console.log(`Chain changed from ${lastChainRef.current} to ${currentChain?.key}`);
      
      // Set changing chain flag
      setIsChangingChain(true);
      
      // Reset swap state when chain changes
      resetSwapState();
      
      // Update the chain reference
      lastChainRef.current = currentChain?.key;
      
      // Clear changing chain flag after a short delay to allow components to update
      const timeout = setTimeout(() => {
        setIsChangingChain(false);
        console.log(`Network change to ${currentChain?.name} completed`);
      }, 500);
      
      return () => clearTimeout(timeout);
    }
  }, [currentChain?.key, currentChain?.name, setExchangeRateError, setExecutionError]);

// Check for token and action parameters from navigation
  useEffect(() => {
    // First check for direct navigation state (from TokenInfoModal)
    if (location.state && location.state.action && location.state.token) {
      const { action, token } = location.state;
      
      console.log(`Setting up swap for ${action} action with token:`, token);
      
      // Get native token for the current chain
      const nativeToken = currentChain ? createNativeTokenFromChain(currentChain) : null;
      
      if (!nativeToken) {
        console.error(t('swap.errors.invalidChainConfig'));
        return;
      }
      
      // Convert token to the format expected by Swap component
      const formattedToken = {
        symbol: token.symbol,
        name: token.name,
        address: token.address,
        decimals: token.decimals || 18
      };
      
      // Set tokens based on action
      if (action === 'buy') {
        // Buy: Native token -> Selected token
        setInputToken(nativeToken);
        setOutputToken(formattedToken);
        console.log("Set up Buy swap:", nativeToken.symbol, "->", formattedToken.symbol);
      } else if (action === 'sell') {
        // Sell: Selected token -> Native token
        setInputToken(formattedToken);
        setOutputToken(nativeToken);
        console.log("Set up Sell swap:", formattedToken.symbol, "->", nativeToken.symbol);
      }
      
      // Reset amounts to ensure they're recalculated
      setAmountIn('');
      setAmountOut('');
      setMinimumAmountOut('');
      
      // Reset error states
      if (setExchangeRateError) setExchangeRateError(null);
      setExecutionError(null);
      
      // Clear the location state to avoid applying this again on future updates
      window.history.replaceState({}, document.title);
    }
    // Then check for deep link token data in session storage
    else {
      const deepLinkSwapData = sessionStorage.getItem('deep_link_swap_token');
      if (deepLinkSwapData) {
        try {
          const swapData = JSON.parse(deepLinkSwapData);
          
          // Log the data we received
          console.log('Applying deep link swap configuration from storage:', swapData);
          
          if (swapData.token && swapData.action) {
            // Get native token for the current chain
            const nativeToken = currentChain ? createNativeTokenFromChain(currentChain) : null;
            
            if (!nativeToken) {
              console.error(t('swap.errors.invalidChainConfig'));
              return;
            }
            
            // Check if we need to load token info (for Telegram deep links)
            if (swapData.token.address && !swapData.token.symbol && sessionStorage.getItem('telegram_token_needs_loading') === 'true') {
              // Load token info asynchronously
              const loadTokenInfo = async () => {
                try {
                  const abi = [
                    "function name() view returns (string)",
                    "function symbol() view returns (string)",
                    "function decimals() view returns (uint8)"
                  ];
                  
                  console.log(`Loading token info for ${swapData.token.address} on ${currentChain.name}`);
                  const tokenContract = new ethers.Contract(swapData.token.address, abi, provider);
                  
                  const [name, symbol, decimals] = await Promise.all([
                    tokenContract.name(),
                    tokenContract.symbol(),
                    tokenContract.decimals()
                  ]);
                  
                  const formattedToken = {
                    symbol,
                    name,
                    address: swapData.token.address,
                    decimals: Number(decimals)
                  };
                  
                  console.log(`Loaded token: ${symbol} (${name}) with ${decimals} decimals`);
                  
                  // Set tokens based on action
                  if (swapData.action === 'buy') {
                    setInputToken(nativeToken);
                    setOutputToken(formattedToken);
                    console.log("Set up Buy swap with loaded token:", nativeToken.symbol, "->", formattedToken.symbol);
                  } else if (swapData.action === 'sell') {
                    setInputToken(formattedToken);
                    setOutputToken(nativeToken);
                    console.log("Set up Sell swap with loaded token:", formattedToken.symbol, "->", nativeToken.symbol);
                  }
                  
                  // Reset amounts
                  setAmountIn('');
                  setAmountOut('');
                  setMinimumAmountOut('');
                  
                  // Reset error states
                  if (setExchangeRateError) setExchangeRateError(null);
                  setExecutionError(null);
                  
                  // Clear the loading flag
                  sessionStorage.removeItem('telegram_token_needs_loading');
                } catch (error) {
                  console.error('Error loading token info:', error);
                  if (setExchangeRateError) {
                    setExchangeRateError(t('swap.errors.tokenLoadFailed'));
                  }
                  // Clear the flags even on error
                  sessionStorage.removeItem('telegram_token_needs_loading');
                }
              };
              
              if (provider && currentChain) {
                loadTokenInfo();
              }
              
              // Clear the stored data
              sessionStorage.removeItem('deep_link_swap_token');
              return;
            }
            
            // If we have full token info, use it directly
            const formattedToken = {
              symbol: swapData.token.symbol || 'Unknown',
              name: swapData.token.name || 'Unknown Token',
              address: swapData.token.address,
              decimals: swapData.token.decimals || 18
            };
            
            // Set tokens based on action
            if (swapData.action === 'buy') {
              // Buy: Native token -> Selected token
              setInputToken(nativeToken);
              setOutputToken(formattedToken);
              console.log("Set up Buy swap from deep link:", nativeToken.symbol, "->", formattedToken.symbol);
            } else if (swapData.action === 'sell') {
              // Sell: Selected token -> Native token
              setInputToken(formattedToken);
              setOutputToken(nativeToken);
              console.log("Set up Sell swap from deep link:", formattedToken.symbol, "->", nativeToken.symbol);
            }
            
            // Reset amounts to ensure they're recalculated
            setAmountIn('');
            setAmountOut('');
            setMinimumAmountOut('');
            
            // Reset error states
            if (setExchangeRateError) setExchangeRateError(null);
            setExecutionError(null);
          }
          
          // Clear the stored data so it doesn't reapply on future navigations
          sessionStorage.removeItem('deep_link_swap_token');
        } catch (error) {
          console.error('Error applying deep link swap configuration:', error);
          sessionStorage.removeItem('deep_link_swap_token');
          sessionStorage.removeItem('telegram_token_needs_loading');
        }
      }
    }
  }, [location, currentChain, setExchangeRateError, setExecutionError, t, provider]);
  // Fetch gas price for the current network
  useEffect(() => {
    if (!provider) return;
    
    const fetchGasPrice = async () => {
      try {
        const feeData = await provider.getFeeData();
        const gasPriceBN = feeData.maxFeePerGas || feeData.gasPrice;
        if (gasPriceBN) {
          const gweiValue = formatUnits(gasPriceBN, 'gwei');
          setDynamicGasPrice(gweiValue);
        }
      } catch (err) {
        console.error("Error fetching gas price:", err);
      }
    };
    
    fetchGasPrice();
  }, [provider, currentChain]);

  // Fetch BitDogSwap fee information when chain changes
  useEffect(() => {
    const fetchAggregatorFees = async () => {
      if (!provider || !currentChain?.aggregatorAddress || currentChain.aggregatorAddress === 'SushiSwap') return;
      
      try {
        const aggregator = getBitDogSwapContract(provider, currentChain.aggregatorAddress, null);
        if (!aggregator) return;
        
        // Get fee percentage
        const feePercentage = await aggregator.getFeePercentage();
        setAggregatorFeePercentage(feePercentage / 100); // Convert to percentage (e.g. 50 -> 0.5%)
        
        // Get fee collection method
        const feesInWeth = await aggregator.getFeeCollectionMethod();
        setCollectFeesInWeth(feesInWeth);
      } catch (err) {
        console.error("Error fetching aggregator fee information:", err);
      }
    };
    
    fetchAggregatorFees();
  }, [provider, currentChain]);

  // Auto-refresh exchange rate every 60 seconds
  useEffect(() => {
    // Set up the refresh timer
    const startRefreshTimer = () => {
      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      
      // Start a new timer if we have both tokens selected and it's not a wrap/unwrap
      if (inputToken.symbol && outputToken.symbol && !wrapUnwrapOperation) {
        refreshTimerRef.current = setInterval(() => {
          // Only trigger a refresh if user has stopped typing and we have a valid amount
          const now = Date.now();
          const isUserTyping = now - lastInputTimeRef.current < 1500; // If less than 1.5s since last input
          
          if (!isUserTyping && amountIn) {
            console.log("Auto-refreshing exchange rate...");
            fetchExchangeRate(true); // Force refresh on timer
          } else {
            console.log("Skipping auto-refresh: user is typing or no amount");
          }
        }, 60000); 
      }
    };
    
    // Start the timer
    startRefreshTimer();
    
    // Clean up on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [inputToken, outputToken, fetchExchangeRate, wrapUnwrapOperation, amountIn]);

// Update the wrap/unwrap detection effect to set the exchange rate directly
useEffect(() => {
  if (inputToken?.symbol && outputToken?.symbol && currentChain?.wrappedNativeToken) {
    // Check if this is a wrap/unwrap operation
    const operation = isWrapUnwrapOperation(inputToken, outputToken);
    
    if (operation) {
      // Immediately set the operation type
      setWrapUnwrapOperation(operation);
      
      // Directly set exchange rate to 1:1 and price impact to 0
      // This ensures we don't trigger any unnecessary API calls
      setExchangeRate(1);
      setPriceImpact(0);
      
      // Clear any errors
      setExchangeRateError(null);
      
      console.log(`Detected ${operation.type} operation - set fixed 1:1 exchange rate`);
    } else {
      setWrapUnwrapOperation(null);
    }
  } else {
    setWrapUnwrapOperation(null);
  }
}, [
  inputToken?.symbol, 
  inputToken?.address,
  outputToken?.symbol, 
  outputToken?.address,
  currentChain?.wrappedNativeToken, 
  isWrapUnwrapOperation,
  setExchangeRate,
  setPriceImpact,
  setExchangeRateError
]);

// Skip exchange rate fetching for wrap/unwrap operations
useEffect(() => {
  // Skip exchange rate fetching if this is a wrap/unwrap operation
  if (wrapUnwrapOperation) {
    // For wrap/unwrap, set a 1:1 exchange rate directly without any API calls
    setExchangeRate(1);
    setPriceImpact(0);
    setExchangeRateError(null);
    return;
  }

  if (inputToken?.symbol && outputToken?.symbol && !isChangingChain) {
    // Check if we need a wallet address (for SushiSwap) and have one
    const isSushiSwapChain = isUsingSushiSwap(currentChain);
    const needsWalletAddress = isSushiSwapChain;
    const hasWalletAddress = !!userWalletAddress;
    
    // Only fetch if we don't need a wallet address or we have one
    if (!needsWalletAddress || hasWalletAddress) {
      const fetchTimeout = setTimeout(() => {
        fetchExchangeRate();
      }, 200);
      
      return () => clearTimeout(fetchTimeout);
    } else if (needsWalletAddress && !hasWalletAddress) {
      // If SushiSwap is needed but we don't have an address, show clear error
      setExchangeRateError(t('swap.errors.connectWallet'));
    }
  }
}, [
  inputToken?.symbol, 
  outputToken?.symbol, 
  fetchExchangeRate, 
  isChangingChain, 
  currentChain, 
  userWalletAddress, 
  setExchangeRateError,
  wrapUnwrapOperation,
  exchangeRate,
  setExchangeRate,
  setPriceImpact,
  t
]);

// Add an effect to handle direct updates when a wrap/unwrap operation is detected
useEffect(() => {
  // When we detect a wrap/unwrap operation and we have input/output amounts
  if (wrapUnwrapOperation && (amountIn || amountOut)) {
    if (lastEdited === 'input' && amountIn) {
      // If user edited input, update output to match
      setAmountOut(amountIn);
      setMinimumAmountOut(amountIn);
    } else if (lastEdited === 'output' && amountOut) {
      // If user edited output, update input to match
      setAmountIn(amountOut);
      setMinimumAmountOut(amountOut);
    }
  }
}, [wrapUnwrapOperation, amountIn, amountOut, lastEdited]);

// Add an effect specifically for maintaining 1:1 ratio for wrap/unwrap
useEffect(() => {
  // If this is a wrap/unwrap, ensure the output amount matches input (1:1 ratio)
  if (wrapUnwrapOperation && amountIn) {
    const num = parseFloat(amountIn);
    if (!isNaN(num)) {
      setAmountOut(amountIn);
      setMinimumAmountOut(amountIn); // No slippage for wrap/unwrap
    } else {
      setAmountOut('');
      setMinimumAmountOut('');
    }
  }
}, [wrapUnwrapOperation, amountIn]);

// Add a clear effect to reset error when wallet changes
useEffect(() => {
  if (userWalletAddress && swapError === t('swap.errors.connectWallet')) {
    setExchangeRateError(null);
  }
}, [userWalletAddress, swapError, setExchangeRateError, t]);

useEffect(() => {
  // Skip for wrap/unwrap operations (handled by dedicated effect)
  if (wrapUnwrapOperation) {
    return;
  }
  
  if (exchangeRate && amountIn && lastEdited === 'input') {
    const num = parseFloat(amountIn);
    if (!isNaN(num)) {
      // Calculate expected output (without slippage) - preserve full precision
      const expectedOut = num * exchangeRate;
      // Don't use toFixed to preserve all decimal places
      setAmountOut(expectedOut.toString());
      
      // Calculate minimum output (with slippage applied)
      const slippageFactor = 1 - (parseFloat(slippage) / 100);
      const minOut = expectedOut * slippageFactor;
      setMinimumAmountOut(minOut.toString());
    }
  } else if (exchangeRate && amountOut && lastEdited === 'output') {
    const num = parseFloat(amountOut);
    if (!isNaN(num)) {
      // Calculate required input - preserve full precision
      const requiredIn = num / exchangeRate;
      // Don't use toFixed to preserve all decimal places
      setAmountIn(requiredIn.toString());
      
      // Calculate minimum output (with slippage applied)
      const slippageFactor = 1 - (parseFloat(slippage) / 100);
      const minOut = num * slippageFactor;
      setMinimumAmountOut(minOut.toString());
    }
  }
}, [exchangeRate, slippage, amountIn, amountOut, lastEdited, wrapUnwrapOperation]);

  const lastInputTimeRef = useRef(Date.now());

// "From" input changed
const handleInputChange = (value) => {
  setAmountIn(value);
  setLastEdited('input');
  setExecutionError(null);
  setWrapUnwrapError(null);
  
  // Reset txnHash when user starts a new transaction
  setTxnHash(null);
  
  const num = parseFloat(value);
  if (!isNaN(num)) {
    if (wrapUnwrapOperation) {
      // For wrap/unwrap, use 1:1 exchange rate - preserve full precision
      setAmountOut(value); // Use original value to preserve decimal places
      setMinimumAmountOut(value); // No slippage for wrap/unwrap
    } else if (exchangeRate) {
      // Calculate expected output (without slippage) - preserve full precision
      const expectedOut = num * exchangeRate;
      // Don't use toFixed to preserve all decimal places
      setAmountOut(expectedOut.toString());
      
      // Calculate minimum output (with slippage applied)
      const slippageFactor = 1 - (parseFloat(slippage) / 100);
      const minOut = expectedOut * slippageFactor;
      setMinimumAmountOut(minOut.toString());
    }
  } else {
    setAmountOut('');
    setMinimumAmountOut('');
  }
};

// "To" input changed
const handleOutputChange = (value) => {
  setAmountOut(value);
  setLastEdited('output');
  setExecutionError(null);
  setWrapUnwrapError(null);
  
  // Reset txnHash when user starts a new transaction
  setTxnHash(null);
  
  const num = parseFloat(value);
  if (!isNaN(num)) {
    if (wrapUnwrapOperation) {
      // For wrap/unwrap, use 1:1 exchange rate - preserve full precision
      setAmountIn(value); // Use original value to preserve decimal places
      setMinimumAmountOut(value); // No slippage for wrap/unwrap
    } else if (exchangeRate) {
      // Calculate required input - preserve full precision
      const requiredIn = num / exchangeRate;
      // Don't use toFixed to preserve all decimal places
      setAmountIn(requiredIn.toString());
      
      // Calculate minimum output (with slippage applied)
      const slippageFactor = 1 - (parseFloat(slippage) / 100);
      const minOut = num * slippageFactor;
      setMinimumAmountOut(minOut.toString());
    }
  } else {
    setAmountIn('');
    setMinimumAmountOut('');
  }
};

// "Max" button => fill from side with the entire inputTokenBalance
const handleMax = () => {
  if (!inputTokenBalance) return;
  
  // If this is a native token (ETH/BROCK), leave some for gas
  if (isNativeToken(inputToken.address)) {
    try {
      // Parse the full balance
      const fullBalance = parseFloat(inputTokenBalance);
      
      // Estimate gas cost (in native token)
      // We'll estimate about 0.01 native token for gas or 5% of balance, whichever is smaller
      const gasEstimate = Math.min(0.01, fullBalance * 0.05);
      
      // Only subtract gas cost if we have enough balance
      const adjustedAmount = fullBalance > gasEstimate ? 
        (fullBalance - gasEstimate).toString() : 
        inputTokenBalance;
      
      setAmountIn(adjustedAmount);
      setLastEdited('input');
      setExecutionError(null);
      setWrapUnwrapError(null);
      
      // Calculate output based on adjusted amount
      const num = parseFloat(adjustedAmount);
      if (!isNaN(num)) {
        if (wrapUnwrapOperation) {
          // For wrap/unwrap, use 1:1 exchange rate - preserve full precision
          setAmountOut(adjustedAmount); // Use original value to preserve decimal places
          setMinimumAmountOut(adjustedAmount); // No slippage for wrap/unwrap
        } else if (exchangeRate) {
          const expectedOut = num * exchangeRate;
          // Don't use toFixed to preserve all decimal places
          setAmountOut(expectedOut.toString());
          
          // Calculate minimum output (with slippage applied)
          const slippageFactor = 1 - (parseFloat(slippage) / 100);
          const minOut = expectedOut * slippageFactor;
          setMinimumAmountOut(minOut.toString());
        }
      }
      
    } catch (error) {
      console.error("Error in max calculation:", error);
      // Fallback to just using the full balance
      setAmountIn(inputTokenBalance);
    }
  } else {
    // For token transfers, use full balance
    setAmountIn(inputTokenBalance);
    setLastEdited('input');
    setExecutionError(null);
    setWrapUnwrapError(null);
    
    // Reset txnHash when user starts a new transaction
    setTxnHash(null);
    
    const num = parseFloat(inputTokenBalance);
    if (!isNaN(num)) {
      if (wrapUnwrapOperation) {
        // For wrap/unwrap, use 1:1 exchange rate - preserve full precision
        setAmountOut(inputTokenBalance); // Use original value to preserve decimal places
        setMinimumAmountOut(inputTokenBalance); // No slippage for wrap/unwrap
      } else if (exchangeRate) {
        // Calculate expected output - preserve full precision
        const expectedOut = num * exchangeRate;
        // Don't use toFixed to preserve all decimal places
        setAmountOut(expectedOut.toString());
        
        // Calculate minimum output (with slippage applied)
        const slippageFactor = 1 - (parseFloat(slippage) / 100);
        const minOut = expectedOut * slippageFactor;
        setMinimumAmountOut(minOut.toString());
      } else {
        setAmountOut('');
        setMinimumAmountOut('');
      }
    }
  }
};

  // Approve token - modified to use modal or direct execution based on autoConfirm
  const handleApproveToken = async () => {
    const result = await approveToken();
    
    // If auto-confirm is disabled and approval was prepared successfully, 
    // the modal will be shown. We don't need to do anything else.
    if (!autoConfirm && result === true) {
      // Modal is already showing, handled by useTokenApproval
    }
  };

  // Function to handle approval confirmation from modal
  const handleConfirmApproval = async (gasSettings) => {
    try {
      // Execute the approval transaction
      const result = await executeApproval(gasSettings);
      
      if (result && result.success) {
        // In case of success, we keep the modal open to show the success state
        // The modal will automatically display the success view because txnHash is set
        console.log("Approval transaction confirmed successfully:", result.hash);
        return { success: true, hash: result.hash };
      } else {
        // If there was an error, the modal would have been closed in executeApproval
        console.error("Approval failed:", result?.error || t('swap.errors.approvalFailed', { message: '' }));
        return { success: false, error: result?.error || t('swap.errors.approvalFailed', { message: '' }) };
      }
    } catch (error) {
      console.error("Error during approval confirmation:", error);
      // Close the approval modal to show error
      setApprovalConfirmationModalOpen(false);
      return { 
        success: false, 
        error: error.message || t('swap.errors.approvalFailed', { message: '' }) 
      };
    }
  };

  // Prepare swap/wrap confirmation
  const handlePrepareSwap = async () => {
    // Clear any previous errors
    setExecutionError(null);
    setWrapUnwrapError(null);

    // Ensure we have the current wallet address
    if (!currentWallet || !currentWallet.address) {
      const errorMsg = t('swap.errors.walletConnectionError');
      setExecutionError(errorMsg);
      return;
    }

    // If this is a wrap/unwrap operation, use the dedicated flow
    if (wrapUnwrapOperation) {
      await handlePrepareWrapUnwrap();
      return;
    }
    
    // User's wallet address - CRITICAL for SushiSwap to work correctly
    const walletAddress = currentWallet.address;
    
    let swapData = null;
    
    // For SushiSwap, we need to get actual transaction data before confirming
    if (isUsingSushiSwap(currentChain)) {
      try {
        console.log("Fetching exchange transaction data for execution...");
        
        // Get the swap function from the API
        const { fetchSushiSwapRoute } = await import('../../services/sushiswapApi');
        
        // We need to fetch real transaction data now that we're actually going to swap
        // This will check allowance and balance
        const swapResponse = await fetchSushiSwapRoute(
          currentChain,
          inputToken, 
          outputToken, 
          amountIn,
          {
            userAddress: walletAddress, 
            slippage: slippage,
            deadline: deadline,
            clientId: 'bitdog-wallet'
          }
        );
        
        // Store the complete response to use directly
        swapData = swapResponse;
        
        console.log("Exchange swap data received:", {
          hasData: !!swapData,
          hasRouteSummary: !!(swapData?.data?.routeSummary),
          hasTransaction: !!(swapData?.data?.tx)
        });
        
        if (!swapData || !swapData.data || !swapData.data.routeSummary) {
          throw new Error(t('swap.errors.missingTransactionData'));
        }
      } catch (error) {
        console.error("Error fetching swap data:", error);
        
        // If it's an approval error, show the approval button
        if (error.message && error.message.includes("allowance")) {
          // Let the approval system handle this
          setExecutionError(t('swap.tokenApprovalNeeded'));
          return;
        }
        
        // Otherwise show the error
        setExecutionError(error.message || t('swap.errors.executionFailed', { message: '' }));
        return;
      }
    }

    try {
      // Now prepare the confirmation with the full transaction data
      let success;
      
      if (swapData) {
        // If we have new swap data from SushiSwap, use it directly
        success = await prepareSwapConfirmation(
          inputToken,
          outputToken,
          amountIn,
          amountOut,
          inputTokenBalance,
          slippage,
          gasOption,
          customGasPrice,
          dynamicGasPrice,
          walletAddress,
          swapData.data.tx.to, // Use the router address from response
          null, // No destRouter for SushiSwap
          priceImpact,
          aggregatorFeePercentage,
          collectFeesInWeth,
          currentChain?.nativeToken?.symbol || 'NATIVE',
          deadline,
          swapData.data.routeSummary // Use the fresh route summary
        );
        
        // Store the new routeSummary for future use if we don't immediately execute
        // This is in case the user confirms the modal later
        window.tempSwapData = swapData;
        
      } else {
        // For non-SushiSwap or when no new data is available, use existing state
        success = await prepareSwapConfirmation(
          inputToken,
          outputToken,
          amountIn,
          amountOut,
          inputTokenBalance,
          slippage,
          gasOption,
          customGasPrice,
          dynamicGasPrice,
          walletAddress,
          bestRouter,
          bestDestRouter,
          priceImpact,
          aggregatorFeePercentage,
          collectFeesInWeth,
          currentChain?.nativeToken?.symbol || 'NATIVE',
          deadline,
          routeSummary
        );
      }

      // If successful and auto-confirm is enabled, execute swap
      if (success && autoConfirm) {
        let swapResult;
        
        if (swapData) {
          // Use the fresh data from SushiSwap
          swapResult = await executeSwap(
            inputToken,
            outputToken,
            amountIn,
            minimumAmountOut,
            slippage,
            gasOption,
            customGasPrice,
            dynamicGasPrice,
            walletAddress,
            deadline,
            swapData.data.routeSummary
          );
        } else {
          // Use existing data
          swapResult = await executeSwap(
            inputToken,
            outputToken,
            amountIn,
            minimumAmountOut,
            slippage,
            gasOption,
            customGasPrice,
            dynamicGasPrice,
            walletAddress,
            deadline,
            routeSummary
          );
        }
        
        if (swapResult && swapResult.success) {
          // Reset form on successful swap
          setAmountIn('');
          setAmountOut('');
          setMinimumAmountOut('');
          
          // Refresh balances
          refreshAfterSwap(swapResult);
        }
      } else if (success) {
        // Show confirmation modal
        setIsConfirmationModalOpen(true);
      }
    } catch (error) {
      console.error("Error preparing swap:", error);
      setExecutionError(error.message || t('swap.errors.preparationFailed', { message: '' }));
    }
  };

// Handle wrap/unwrap preparation 
const handlePrepareWrapUnwrap = async () => {
  // Validate that we have a valid wrap/unwrap operation
  if (!wrapUnwrapOperation) {
    setWrapUnwrapError("Invalid operation");
    return;
  }

  // Validate amount
  if (!amountIn || parseFloat(amountIn) <= 0) {
    setWrapUnwrapError("Please enter a valid amount");
    return;
  }

  // Validate balance
  const balance = parseFloat(inputTokenBalance);
  if (parseFloat(amountIn) > balance) {
    setWrapUnwrapError("Insufficient balance");
    return;
  }

  // Clear previous errors
  setWrapUnwrapError(null);

  // Get the current operation to ensure fresh data
  const currentOperation = getCurrentOperation();
  if (!currentOperation) {
    setWrapUnwrapError("Invalid operation - tokens have changed");
    return;
  }

  const { type, nativeToken, wrappedToken } = currentOperation;

  try {
    // Prepare wrap/unwrap confirmation - now returns {success, details}
    const prepResult = await prepareWrapUnwrapConfirmation(
      type,
      nativeToken,
      wrappedToken,
      amountIn,
      customGasPrice || dynamicGasPrice
    );
    
    if (!prepResult.success) {
      setWrapUnwrapError("Failed to prepare transaction");
      return;
    }

    // If successful and auto-confirm is enabled, execute operation immediately
    if (autoConfirm) {
      console.log(`Auto-confirming with amount: ${amountIn}`);
      // IMPORTANT: Pass the details directly to avoid relying on state updates
      const result = await handleWrapUnwrapConfirmation({}, amountIn, prepResult.details);
      
      if (result && result.success) {
        // Reset form on successful operation 
        setAmountIn('');
        setAmountOut('');
        setMinimumAmountOut('');
        
        // Refresh balances
        refreshAfterSwap({ success: true });
      }
    } else {
      // If auto-confirm is disabled but preparation was successful, show the confirmation modal
      // The confirmation details should be set in state now
      setIsWrapUnwrapModalOpen(true);
    }
  } catch (error) {
    console.error("Error preparing wrap/unwrap:", error);
    setWrapUnwrapError(error.message || "Unknown error occurred");
  }
};

// Create a wrapper function that passes the current amount to handleWrapUnwrapConfirmation
const handleWrapUnwrapConfirmationWithAmount = async (gasSettings) => {
  console.log(`Calling handleWrapUnwrapConfirmation with current amount: ${amountIn}`);
  const result = await handleWrapUnwrapConfirmation(gasSettings, amountIn); // Pass just the current amount
  
  // If the transaction was successful, refresh balances
  if (result && result.success) {
    console.log("Wrap/unwrap operation successful, refreshing balances...");
    // Reset form
    setAmountIn('');
    setAmountOut('');
    setMinimumAmountOut('');
    
    // Refresh balances with delay to allow chain to update
    setTimeout(async () => {
      try {
        await refreshAllBalances();
        console.log("Balances refreshed after wrap/unwrap");
      } catch (error) {
        console.error("Error refreshing balances after wrap/unwrap:", error);
      }
    }, 2000); // Increased delay for better reliability
  }
  
  return result;
};
const refreshAfterSwap = useCallback(async (swapResult) => {
  if (!swapResult || !swapResult.success) {
    return; // Don't refresh on failed swaps
  }
  
  console.log("Refreshing balances after successful operation");
  
  // Clear input/output fields
  setAmountIn('');
  setAmountOut('');
  setMinimumAmountOut('');
  
  // Slightly longer delay to ensure blockchain state is updated
  setTimeout(async () => {
    try {
      // First refresh the specific tokens involved
      if (inputToken?.symbol) {
        console.log(`Refreshing balance for ${inputToken.symbol}`);
        await refreshInputBalance();
      }
      
      if (outputToken?.symbol) {
        console.log(`Refreshing balance for ${outputToken.symbol}`);
        await refreshOutputBalance();
      }
      
      // Then refresh all balances to be thorough
      await refreshAllBalances();
      console.log("All balances refreshed");
    } catch (error) {
      console.error("Error refreshing balances:", error);
    }
  }, 1000); // 1 second delay for better reliability
}, [refreshInputBalance, refreshOutputBalance, refreshAllBalances, inputToken, outputToken]);

  // Handle swap confirmation from modal
  const handleConfirmSwap = async (gasSettings) => {
    // Ensure we have the current wallet address
    if (!currentWallet || !currentWallet.address) {
      setExecutionError(t('swap.errors.walletConnectionError'));
      return { success: false, error: t('swap.errors.walletConnectionError') };
    }
    
    // User's wallet address - CRITICAL for SushiSwap to work correctly
    const walletAddress = currentWallet.address;
    
    try {
      // Check if we have saved swap data from the preparation step
      const savedSwapData = window.tempSwapData;
      
      let result;
      if (savedSwapData && isUsingSushiSwap(currentChain)) {
        // Use the saved data for SushiSwap
        console.log("Using saved exchange data for confirmation");
        
        result = await executeSwap(
          inputToken,
          outputToken,
          amountIn,
          minimumAmountOut,
          slippage,
          gasSettings?.gasOption || gasOption,
          gasSettings?.gasPrice || customGasPrice,
          dynamicGasPrice,
          walletAddress, // Use wallet address
          deadline,
          savedSwapData.data.routeSummary
        );
        
        // Clear the saved data after use
        window.tempSwapData = null;
      } else {
        // Use existing data for other chains
        result = await executeSwap(
          inputToken,
          outputToken,
          amountIn,
          minimumAmountOut,
          slippage,
          gasSettings?.gasOption || gasOption,
          gasSettings?.gasPrice || customGasPrice,
          dynamicGasPrice,
          walletAddress, // Use wallet address
          deadline,
          routeSummary
        );
      }
    
      if (result && result.success) {
        // Reset form on successful swap
        setAmountIn('');
        setAmountOut('');
        setMinimumAmountOut('');
        
        // Refresh balances
        refreshAfterSwap(result);
      }
    
      return result;
    } catch (error) {
      console.error("Error executing swap:", error);
      setExecutionError(error.message || t('swap.errors.executionFailed', { message: '' }));
      return { 
        success: false, 
        error: error.message || t('swap.errors.executionFailed', { message: '' }) 
      };
    }
  };

  // Reverse tokens and amounts - UPDATED to close any open modals
  const reverseTokens = () => {
    if (!inputToken.symbol || !outputToken.symbol) return;
    
    // Create deep copies of the token objects
    const inputCopy = {...outputToken};
    const outputCopy = {...inputToken};
    
    // Update state with copies
    setInputToken(inputCopy);
    setOutputToken(outputCopy);

    // Swap amounts
    const tmpAmount = amountIn;
    setAmountIn(amountOut);
    setAmountOut(tmpAmount);
    
    // Recalculate minimum amount out
    if (amountOut && exchangeRate) {
      const slippageFactor = 1 - (parseFloat(slippage) / 100);
      const minOut = parseFloat(amountIn) * slippageFactor;
      setMinimumAmountOut(minOut.toFixed(6));
    } else {
      setMinimumAmountOut('');
    }
    
    // Reset errors and transaction hash
    setExecutionError(null);
    setWrapUnwrapError(null);
    setTxnHash(null);
    
    // CRITICAL FIX: Close any open confirmation modals
    // This ensures the user can't confirm an operation with stale data
    setIsConfirmationModalOpen(false);
    setIsWrapUnwrapModalOpen(false);
  };

  // Open token selection modal
  const openTokenModal = (side) => {
    setSelectedSide(side);
    setIsTokenModalOpen(true);
  };

  // Handle token selection from modal
  const handleSelectToken = (modalToken) => {
    // Get the exact decimals from the token
    const tokenDecimals = modalToken.decimals; // Don't convert/sanitize - keep original value
    
    const chosenToken = {
      symbol: modalToken.symbol,
      address: modalToken.address,
      name: modalToken.name,
      decimals: tokenDecimals, // Keep original decimals
      icon: modalToken.logoURI || ''
    };
  
    console.log(`Selected ${chosenToken.symbol} with ${tokenDecimals} decimals`);

    // If user picks the same token on the other side => reverse
    const otherSide = (selectedSide === 'input') ? outputToken : inputToken;
    if (
      chosenToken.address &&
      otherSide.address &&
      chosenToken.address.toLowerCase() === otherSide.address.toLowerCase()
    ) {
      reverseTokens();
      setIsTokenModalOpen(false);
      return;
    }

    if (selectedSide === 'input') {
      setInputToken(chosenToken);
    } else {
      setOutputToken(chosenToken);
    }
    
    // Reset amounts when tokens change
    setAmountIn('');
    setAmountOut('');
    setMinimumAmountOut('');
    setExecutionError(null);
    setWrapUnwrapError(null);
    
    setIsTokenModalOpen(false);
  };

  // Get formatted minimum amount received for display
  const getFormattedMinimumReceived = () => {
    if (!minimumAmountOut || !outputToken.symbol) return null;
    
    return (
      <div className="minimum-received">
        <span className="minimum-received-label">{t('swap.minimumReceived')}:</span>
        <span className="minimum-received-value">
          {formatAmountForDisplay(minimumAmountOut)} {outputToken.symbol}
        </span>
      </div>
    );
  };

// Update the return statement in Swap.js to use the new wrapper structure
return (
  <div className="swap-page">
    {/* Main content area - make this scrollable */}
    <div className="swap-content-wrapper">
      <div className="swap-card">
        {/* Header with network and wallet information */}
        <div className="swap-header">
          <SwapHeader 
            currentChain={currentChain}
            currentWallet={currentWallet}
          />
        </div>

        {/* No info box needed */}

        {/* Swap Form Component */}
        <SwapForm
          inputToken={inputToken}
          outputToken={outputToken}
          onInputTokenSelect={() => openTokenModal('input')}
          onOutputTokenSelect={() => openTokenModal('output')}
          inputTokenBalance={inputTokenBalance}
          outputTokenBalance={outputTokenBalance}
          amountIn={amountIn}
          amountOut={amountOut}
          onAmountInChange={handleInputChange}
          onAmountOutChange={handleOutputChange}
          exchangeRate={wrapUnwrapOperation ? 1 : exchangeRate}
          priceImpact={wrapUnwrapOperation ? 0 : priceImpact}
          isRefreshing={isRefreshing}
          onManualRefresh={wrapUnwrapOperation ? null : handleManualRefresh}
          isLoading={isLoading}
          isApproving={isApproving}
          isSwapping={isSwapping || isWrapUnwrapping}
          swapError={combinedSwapError}
          slippage={slippage}
          onReverseTokens={reverseTokens}
          handleMax={handleMax}
        />

        {/* Minimum Amount Received */}
        {minimumAmountOut && outputToken.symbol && (
          <div className="swap-minimum-received">
            <div className="minimum-received">
              <span className="minimum-received-label">
                {wrapUnwrapOperation ? "You will receive" : t('swap.minimumReceived')}:
              </span>
              <span className="minimum-received-value">
                {formatAmountForDisplay(minimumAmountOut)} {outputToken.symbol}
              </span>
            </div>
          </div>
        )}

        {/* Swap Settings Component - Hide for wrap/unwrap operations */}
        {!wrapUnwrapOperation && (
          <SwapSettings
            slippage={slippage}
            aggregatorFeePercentage={aggregatorFeePercentage}
            collectFeesInWeth={collectFeesInWeth}
            deadline={deadline}
            showSummary={inputToken.symbol && outputToken.symbol}
          />
        )}

        {/* Swap Actions Component */}
        <SwapActions
          inputToken={inputToken}
          outputToken={outputToken}
          inputTokenBalance={inputTokenBalance}
          amountIn={amountIn}
          needsApproval={wrapUnwrapOperation ? false : needsApproval}
          isCheckingApproval={isCheckingApproval}
          isApproving={isApproving}
          isLoading={isLoading}
          isSwapping={isSwapping || isWrapUnwrapping}
          onApproveToken={handleApproveToken}
          onPrepareSwap={handlePrepareSwap}
          onRefreshApproval={refreshApprovalStatus}
          isWrapUnwrap={!!wrapUnwrapOperation}
          wrapUnwrapType={wrapUnwrapOperation?.type}
        />
      </div>
    </div>

    {/* Token Selection Modal */}
    <TokenListModal
      isOpen={isTokenModalOpen}
      onClose={() => setIsTokenModalOpen(false)}
      onSelectToken={handleSelectToken}
      chain={currentChain?.key || 'ethereum'}
    />

    {/* Swap Confirmation Modal with gas settings support */}
    <ConfirmationModal
      isOpen={isConfirmationModalOpen}
      onClose={() => {
        setIsConfirmationModalOpen(false);
        // Reset gas settings and txnHash when closing modal
        if (!isSwapping) {
          setTxnHash(null);
        }
      }}
      onConfirm={handleConfirmSwap}
      transactionDetails={confirmationDetails}
      txnHash={txnHash}
      explorerUrl={currentChain?.explorerUrl || ''}
      currentGasOption={tempGasOption || gasOption}
      currentGasPrice={tempGasPrice || customGasPrice}
      dynamicGasPrice={dynamicGasPrice}
      onGasSettingChange={handleGasSettingChange}
    />
    
    {/* Wrap/Unwrap Confirmation Modal */}
    <ConfirmationModal
  isOpen={isWrapUnwrapModalOpen}
  onClose={() => {
    setIsWrapUnwrapModalOpen(false);
    if (!isWrapUnwrapping) {
      setWrapUnwrapError(null);
    }
  }}
  onConfirm={handleWrapUnwrapConfirmationWithAmount} // Use the wrapper function
  transactionDetails={wrapUnwrapConfirmationDetails}
  txnHash={wrapUnwrapTxnHash}
  explorerUrl={currentChain?.explorerUrl || ''}
  currentGasOption={gasOption}
  currentGasPrice={customGasPrice}
  dynamicGasPrice={dynamicGasPrice}
  onGasSettingChange={(option, price) => {
    console.log("Wrap/Unwrap gas settings changed:", option, price);
  }}
/>
    
    {/* Approval Confirmation Modal */}
    <ConfirmationModal
      isOpen={approvalConfirmationModalOpen}
      onClose={() => {
        setApprovalConfirmationModalOpen(false);
      }}
      onConfirm={handleConfirmApproval}
      transactionDetails={approvalConfirmationDetails}
      txnHash={approvalTxnHash} // Use the transaction hash from approval hook
      explorerUrl={currentChain?.explorerUrl || ''}
      currentGasOption={gasOption}
      currentGasPrice={customGasPrice}
      dynamicGasPrice={dynamicGasPrice}
      onGasSettingChange={(option, price) => {
        console.log("Approval gas settings changed:", option, price);
      }}
      currentChain={currentChain} // Add currentChain for proper currency display
    />

    {/* Transaction Notification for Auto-confirm */}
    <TransactionNotificationWrapper ref={notificationRef} />
  </div>
);
};

export default Swap;